/**
 * @fileoverview EntryDetailSheet — the expanded view of one time entry
 * (MOR-906). Mirrors the task side panel (`key-date-side-panel.tsx`) so both
 * detail surfaces read the same: identity + badges + date in the header, a
 * tabbed body (Details / Notes / History) on labelled field rows, and the
 * workflow transitions the viewer may perform in the footer.
 *
 * Owners submit and recall; only approvers approve, reject or reverse an
 * approval. Opened by clicking an entry row on the timesheet.
 *
 * While an entry is the owner's own draft (or came back rejected) every field
 * is editable here on the same controls the timesheet table uses, committing
 * per field as the table does. Editing lives in the Details tab rather than
 * the header, so there is one place to look regardless of the field.
 *
 * Dependencies:
 * - Convex: timesheets.entries.{entryDetail, entryContext, updateEntry,
 *   deleteEntry, submitEntry, recallEntry, approveEntries, unapproveEntries,
 *   rejectEntries}, timesheets.notes.*, users.queries.listUsersByIds
 * - Shared UI: Sheet, Tabs, ChangeHistory, NotesFeedView, CategoryBadge
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  Banknote,
  Briefcase,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock,
  Flag,
  GraduationCap,
  Layers,
  Send,
  Tag,
  Tags,
  Timer,
  Trash2,
  Undo2,
  UserPlus,
  XCircle,
  type LucideIcon,
} from "lucide-react";

import type { FunctionReturnType } from "convex/server";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import { roundUnits } from "@cvx/timesheets/units";
import { entryEndTime } from "@cvx/timesheets/clock";

import { extractErrorMessage } from "@/utils/errors";
import { Badge, Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import { Spinner } from "@/ui/primitives/spinner";
import { Switch } from "@/ui/primitives/switch";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { TimePicker } from "@/ui/composed/inputs/time-picker";
import { DictationButton } from "@/ui/composed/inputs/dictation-button";
import { useDictatedField } from "@/ui/hooks/use-dictated-field";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/ui/composed/overlays/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import {
  ChangeHistory,
  type ChangeHistoryEntry,
  type HistoryFieldFormatters,
  type HistoryFieldLabels,
  type ResolvedHistoryUser,
} from "@/components/jobs/phases/gather/summarization/shared-panels/ChangeHistory";
import { NotesFeedView } from "@/components/contacts/notes/NotesFeedView";
import { EntryEvidenceSection } from "./EntryEvidenceSection";
import type { FeedNote } from "@/components/contacts/notes/NoteRow";
import { CategoryBadge, CategoryGlyph } from "./CategoryBadge";
import { NARRATIVE_INPUT_PROPS } from "./narrativeCheck";
import { NarrativeWarningNote } from "./NarrativeWarningNote";
import { formatRate } from "./rateCards.helpers";
import { RateWorkingNote } from "./RateWorkingNote";
import { SubCategoryPicker } from "./SubCategoryPicker";
import { formatAud, formatUnitsAsHours } from "./format";

type EntryStatus = "draft" | "submitted" | "approved" | "rejected";
type ProjectCode = "pre" | "post";

type EntryDetail = NonNullable<
  FunctionReturnType<typeof api.timesheets.entries.entryDetail>
>;

/** The subset of `updateEntry`'s patch this panel writes. */
type EntryPatch = {
  jobId?: Id<"jobs">;
  date?: string;
  startTime?: string | null;
  endTime?: string | null;
  categoryId?: Id<"timeCategories">;
  subCategory?: string | null;
  narrative?: string;
  units?: number;
  billable?: boolean;
  projectCode?: ProjectCode;
};

const STATUS_BADGE: Record<EntryStatus, { label: string; color: string }> = {
  draft: { label: "Draft", color: "gray" },
  submitted: { label: "Submitted", color: "blue" },
  approved: { label: "Approved", color: "emerald" },
  rejected: { label: "Rejected", color: "red" },
};

const ENTRY_FIELD_LABELS: HistoryFieldLabels = {
  date: "Date",
  startTime: "Start time",
  endTime: "End time",
  jobId: "Matter",
  categoryId: "Category",
  subCategory: "Sub-code",
  narrative: "Narrative",
  units: "Units",
  billable: "Billable",
  projectCode: "Project code",
  status: "Status",
  rejectionReason: "Rejection reason",
  writeOffReason: "Write-off reason",
};

/** " · 09:00–10:30", or just the start, or nothing at all. */
function formatWindow(start?: string, end?: string): string {
  if (!start) return end ? ` · until ${end}` : "";
  return end ? ` · ${start}–${end}` : ` at ${start}`;
}

const ENTRY_FIELD_FORMATTERS: HistoryFieldFormatters = {
  billable: (v) => (v ? "Billable" : "Non-billable"),
  projectCode: (v) => (typeof v === "string" ? v.toUpperCase() : "—"),
  units: (v) => (typeof v === "number" ? String(v) : "—"),
};

export function EntryDetailSheet({
  entryId,
  open,
  onOpenChange,
  unitMinutes,
  categoryVisual,
}: {
  entryId: Id<"timeEntries"> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unitMinutes: number;
  /** Colour/icon lookup by category id, provided by the timesheet page. */
  categoryVisual: (categoryId: Id<"timeCategories"> | undefined) => {
    color?: string;
    icon?: string;
  };
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[34rem]"
      >
        {/*
          The body is a child so the entry query is only ever built with a real
          id. `convexQuery` opens a Convex subscription as soon as its descriptor
          is in the query cache, whether or not `enabled` is set, so passing a
          placeholder id here had the server rejecting `""` on every reconnect.
        */}
        {entryId ? (
          <EntryDetailBody
            entryId={entryId}
            unitMinutes={unitMinutes}
            categoryVisual={categoryVisual}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <div className="flex h-64 items-center justify-center">
            <Spinner className="text-muted-foreground" />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function EntryDetailBody({
  entryId,
  unitMinutes,
  categoryVisual,
  onClose,
}: {
  entryId: Id<"timeEntries">;
  unitMinutes: number;
  categoryVisual: (categoryId: Id<"timeCategories"> | undefined) => {
    color?: string;
    icon?: string;
  };
  /** Called after a delete, since the entry the panel describes is gone. */
  onClose: () => void;
}) {
  const { data: detail } = useQuery(
    convexQuery(api.timesheets.entries.entryDetail, { entryId }),
  );

  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );

  const updateEntry = useMutation(api.timesheets.entries.updateEntry);
  const deleteEntry = useMutation(api.timesheets.entries.deleteEntry);
  const submitEntry = useMutation(api.timesheets.entries.submitEntry);
  const recallEntry = useMutation(api.timesheets.entries.recallEntry);
  const approveEntries = useMutation(api.timesheets.entries.approveEntries);
  const unapproveEntries = useMutation(api.timesheets.entries.unapproveEntries);
  const rejectEntries = useMutation(api.timesheets.entries.rejectEntries);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
    } catch (err) {
      toast.error(extractErrorMessage(err, "That didn't work — try again."));
    } finally {
      setBusy(false);
    }
  };

  // Edits commit field by field, as they do on the timesheet table, so there
  // is no draft-of-a-draft to save and nothing to lose by closing the panel.
  const patchEntry = (patch: EntryPatch): void => {
    void updateEntry({ entryId, patch }).catch((err) =>
      toast.error(extractErrorMessage(err, "Could not update the entry.")),
    );
  };

  if (!detail) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  const value =
    detail.hourlyRate !== undefined && detail.billable
      ? formatAud((detail.units * unitMinutes * detail.hourlyRate) / 60)
      : null;

  // The transitions this viewer may perform, most consequential first.
  const actions: Array<{
    key: string;
    label: string;
    icon: LucideIcon;
    variant: "default" | "outline";
    onClick: () => void;
  }> = [];
  if (
    detail.isOwner &&
    (detail.status === "draft" || detail.status === "rejected")
  ) {
    actions.push({
      key: "submit",
      label: "Submit for approval",
      icon: Send,
      variant: "default",
      onClick: () =>
        void run(() => submitEntry({ entryId }), "Submitted for approval."),
    });
  }
  if (detail.canApprove && detail.status === "submitted") {
    actions.push({
      key: "approve",
      label: "Approve",
      icon: CheckCircle2,
      variant: "default",
      onClick: () =>
        void run(
          () => approveEntries({ entryIds: [entryId] }),
          "Entry approved.",
        ),
    });
  }
  if (detail.canApprove && detail.status === "approved" && !detail.writtenOff) {
    actions.push({
      key: "reverse",
      label: "Reverse approval",
      icon: Undo2,
      variant: "outline",
      onClick: () =>
        void run(
          () => unapproveEntries({ entryIds: [entryId] }),
          "Approval reversed — back in the queue.",
        ),
    });
  }
  if (detail.isOwner && detail.status === "submitted") {
    actions.push({
      key: "recall",
      label: "Recall to draft",
      icon: Undo2,
      variant: "outline",
      onClick: () =>
        void run(() => recallEntry({ entryId }), "Recalled to draft."),
    });
  }
  const canReject = detail.canApprove && detail.status === "submitted";

  // When nothing is available, say why rather than showing a dead footer.
  const footerHint: string | null =
    actions.length === 0 && !canReject
      ? detail.status === "approved" && detail.writtenOff
        ? "Restore the write-off before changing this status."
        : detail.status === "approved"
          ? "Only a timesheet approver can change an approved entry."
          : detail.status === "submitted"
            ? "Waiting on a timesheet approver."
            : null
      : null;

  return (
    <>
      <SheetHeader className="border-b p-6">
        <div className="min-w-0 pr-6">
          <SheetTitle className="text-left">
            {detail.kind === "firm"
              ? "Firm activity"
              : `${detail.jobCode ? `${detail.jobCode} — ` : ""}${detail.jobName}`}
          </SheetTitle>
        </div>
        <SheetDescription className="text-left">
          Recorded by {detail.userName}
        </SheetDescription>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Badge
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            color={STATUS_BADGE[detail.status].color as any}
            variant="soft"
          >
            {STATUS_BADGE[detail.status].label}
          </Badge>
          <CategoryBadge
            category={
              {
                name: detail.categoryName,
                ipsCode: undefined,
                ...categoryVisual(detail.categoryId),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            }
          />
          {!detail.billable && <Badge variant="outline">Non-billable</Badge>}
          {detail.writtenOff && (
            <Badge color="red" variant="outline">
              Written off
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 pt-2 text-sm font-medium text-foreground">
          <CalendarDays className="size-4 text-muted-foreground" />
          <span>
            {format(parseISO(detail.date), "EEE, d MMM yyyy")}
            {formatWindow(detail.startTime, detail.endTime)}
          </span>
        </div>
      </SheetHeader>

      <Tabs defaultValue="details" className="flex min-h-0 flex-1 flex-col">
        <div className="px-6 pt-3">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
        </div>
        <ScrollArea className="min-h-0 flex-1">
          <TabsContent value="details" className="mt-0 px-6 py-5">
            <div className="space-y-6 text-sm">
              <div>
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                  Narrative
                </h4>
                {detail.canEdit ? (
                  <NarrativeField
                    value={detail.narrative}
                    units={detail.units}
                    unitMinutes={unitMinutes}
                    orgId={detail.orgId}
                    onCommit={(narrative) => patchEntry({ narrative })}
                  />
                ) : (
                  <p className="mt-1.5 whitespace-pre-wrap leading-relaxed text-foreground">
                    {detail.narrative}
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                  Details
                </h4>
                {detail.canEdit ? (
                  <EditableEntryFields
                    detail={detail}
                    unitMinutes={unitMinutes}
                    onPatch={patchEntry}
                  />
                ) : (
                  <>
                    <DetailRow icon={Clock} label="Time">
                      {detail.units} units (
                      {formatUnitsAsHours(detail.units, unitMinutes)})
                    </DetailRow>
                    <DetailRow icon={Timer} label="Start / end">
                      {detail.startTime
                        ? `${detail.startTime} to ${detail.endTime ?? entryEndTime({ startTime: detail.startTime, units: detail.units }, unitMinutes)}`
                        : "Posted to the day"}
                    </DetailRow>
                    {/* Firm time is never billed and carries no matter
                        phase, so the billable flag and project code do not
                        apply to it. */}
                    {detail.kind !== "firm" && (
                      <>
                        <DetailRow icon={Flag} label="Billable">
                          {detail.billable ? "Billable" : "Non-billable"}
                        </DetailRow>
                        <DetailRow icon={Layers} label="Project code">
                          {detail.projectCode === "pre"
                            ? "Pre-appointment"
                            : "Post-appointment"}
                        </DetailRow>
                      </>
                    )}
                    {detail.subCategory && (
                      <DetailRow icon={Tags} label="Sub-code">
                        {detail.subCategory}
                      </DetailRow>
                    )}
                  </>
                )}
                {value && (
                  <DetailRow icon={Banknote} label="Value">
                    {value}
                  </DetailRow>
                )}
                {detail.hourlyRate !== undefined && (
                  <DetailRow icon={Tag} label="Rate">
                    <div>
                      {formatRate(detail.hourlyRate)}
                      <RateWorkingNote working={detail} />
                    </div>
                  </DetailRow>
                )}
                {detail.gradeName && (
                  <DetailRow icon={GraduationCap} label="Grade">
                    {detail.gradeName}
                  </DetailRow>
                )}
                <DetailRow icon={UserPlus} label="Recorded by">
                  {detail.userName}
                </DetailRow>
              </div>

              {detail.isOwner && <EntryEvidenceSection entryId={entryId} />}

              {detail.status === "rejected" && detail.rejectionReason && (
                <Callout title="Rejection reason">
                  {detail.rejectionReason}
                </Callout>
              )}
              {detail.writtenOff && detail.writeOffReason && (
                <Callout title="Write-off reason">
                  {detail.writeOffReason}
                </Callout>
              )}
            </div>
          </TabsContent>

          <TabsContent value="notes" className="mt-0 px-6 py-5">
            <EntryNotes entryId={entryId} currentUserId={currentUser?._id} />
          </TabsContent>

          <TabsContent value="history" className="mt-0 px-6 py-5">
            <EntryHistory history={detail.history} />
          </TabsContent>
        </ScrollArea>
      </Tabs>

      {(actions.length > 0 || canReject || detail.canEdit || footerHint) && (
        <SheetFooter className="flex flex-row flex-wrap items-center justify-between gap-2 border-t p-6">
          <div className="flex flex-wrap gap-2">
            {actions.map(({ key, label, icon: Icon, variant, onClick }) => (
              <Button
                key={key}
                type="button"
                size="sm"
                variant={variant}
                disabled={busy}
                onClick={onClick}
              >
                <Icon className="size-4" />
                {label}
              </Button>
            ))}
            {footerHint && (
              <p className="text-xs text-muted-foreground">{footerHint}</p>
            )}
          </div>

          {canReject && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              disabled={busy}
              onClick={() => setRejectOpen(true)}
            >
              <XCircle className="size-4" />
              Reject…
            </Button>
          )}

          {detail.canEdit && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await deleteEntry({ entryId });
                  onClose();
                }, "Entry deleted.")
              }
            >
              <Trash2 className="size-4" />
              Delete
            </Button>
          )}
        </SheetFooter>
      )}

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject this entry</DialogTitle>
            <DialogDescription>
              The reason is shown to the team member so they can correct and
              resubmit.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="entry-reject-reason">Reason</Label>
            <Textarea
              id="entry-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim() || busy}
              onClick={() =>
                void run(async () => {
                  await rejectEntries({
                    entryIds: [entryId],
                    reason: rejectReason.trim(),
                  });
                  setRejectOpen(false);
                  setRejectReason("");
                }, "Entry rejected.")
              }
            >
              Reject entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Editing — the same controls, and the same commit-per-field behaviour, that
// the timesheet table offers on a draft row
// ---------------------------------------------------------------------------

/** What the pickers need of a category: identity, colour and its sub-codes. */
type EditableCategory = {
  _id: Id<"timeCategories">;
  name: string;
  color?: TaskColor;
  icon?: string;
  subCodes: Array<{ code: string; name?: string }>;
};

function EditableEntryFields({
  detail,
  unitMinutes,
  onPatch,
}: {
  detail: EntryDetail;
  unitMinutes: number;
  onPatch: (patch: EntryPatch) => void;
}) {
  const { data: context } = useQuery(
    convexQuery(api.timesheets.entries.entryContext, {
      orgId: detail.orgId,
      includeJobId: detail.jobId,
    }),
  );

  // Firm time is coded against a firm activity (a firm-scoped category) and
  // carries no matter, billable flag or project code.
  const isFirm = detail.kind === "firm";

  const [units, setUnits] = useState(String(detail.units));
  useEffect(() => setUnits(String(detail.units)), [detail.units]);

  const [subCategory, setSubCategory] = useState(detail.subCategory ?? "");
  useEffect(
    () => setSubCategory(detail.subCategory ?? ""),
    [detail.subCategory],
  );

  // The entry's own matter and category head the fallback list, so the pickers
  // read correctly before the context query lands.
  const jobOptions = useMemo(() => {
    const own = {
      value: detail.jobId as string,
      label: detail.jobCode
        ? `${detail.jobCode} — ${detail.jobName}`
        : detail.jobName,
      title: detail.jobName,
    };
    if (!context) return [own];

    // Recent matters first, matching the order the timesheet table offers.
    const recentRank = new Map(
      context.recentJobIds.map((id, i) => [id as string, i]),
    );
    return [...context.jobs]
      .sort((a, b) => {
        const ra = recentRank.get(a._id) ?? Infinity;
        const rb = recentRank.get(b._id) ?? Infinity;
        if (ra !== rb) return ra - rb;
        return a.name.localeCompare(b.name);
      })
      .map((j) => ({
        value: j._id as string,
        label: j.jobCode ? `${j.jobCode} — ${j.name}` : j.name,
        title: j.name,
      }));
  }, [context, detail.jobId, detail.jobCode, detail.jobName]);

  const categories = useMemo<EditableCategory[]>(
    () =>
      (isFirm ? context?.firmActivities : context?.categories) ?? [
        { _id: detail.categoryId, name: detail.categoryName, subCodes: [] },
      ],
    [context, detail.categoryId, detail.categoryName, isFirm],
  );

  const categoryOptions = useMemo(
    () =>
      categories.map((c) => ({
        value: c._id as string,
        label: c.name,
        leading: <CategoryGlyph color={c.color} icon={c.icon} />,
      })),
    [categories],
  );

  const commitUnits = () => {
    const next = roundUnits(Number(units), context?.roundingMode);
    if (Number.isFinite(next) && next >= 1 && next !== detail.units) {
      onPatch({ units: next });
    } else {
      setUnits(String(detail.units));
    }
  };

  return (
    <>
      <DetailRow icon={Briefcase} label="Matter">
        {isFirm ? (
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Building2 className="size-3.5" />
            Firm activity — non-chargeable
          </span>
        ) : (
          <SearchableSelect
            value={detail.jobId as string}
            onValueChange={(value) => onPatch({ jobId: value as Id<"jobs"> })}
            options={jobOptions}
            searchPlaceholder="Search matters…"
            emptyMessage="No matters found."
            className="w-full"
          />
        )}
      </DetailRow>
      <DetailRow icon={CalendarDays} label="Date">
        <DatePicker
          value={detail.date}
          onChange={(iso) => {
            if (iso) onPatch({ date: iso });
          }}
          inputClassName="h-8 rounded-lg py-0 text-xs"
        />
      </DetailRow>
      {/*
        Optional clock record of when the work happened — the stopwatch fills
        it in, typing does not. Units stay the billable duration either way.
      */}
      <DetailRow icon={Timer} label="Start / end">
        <div className="flex items-center gap-1.5">
          <TimePicker
            value={detail.startTime ?? undefined}
            onChange={(value) => onPatch({ startTime: value || null })}
            placeholder="Optional"
            clearable
            className="w-[7.5rem]"
            inputClassName="h-8 rounded-lg py-0 text-xs"
          />
          <span className="text-xs text-muted-foreground">–</span>
          <TimePicker
            value={detail.endTime ?? undefined}
            onChange={(value) => onPatch({ endTime: value || null })}
            placeholder="Optional"
            clearable
            className="w-[7.5rem]"
            inputClassName="h-8 rounded-lg py-0 text-xs"
          />
        </div>
      </DetailRow>
      <DetailRow icon={Tag} label="Category">
        <SearchableSelect
          value={detail.categoryId as string}
          onValueChange={(value) => {
            const next = value as Id<"timeCategories">;
            // A sub-code belongs to one head category, so it is dropped
            // unless the new category defines it too.
            const keeps = categories
              .find((c) => c._id === next)
              ?.subCodes.some((s) => s.code === detail.subCategory);
            onPatch({
              categoryId: next,
              ...(detail.subCategory && !keeps ? { subCategory: null } : {}),
            });
          }}
          options={categoryOptions}
          searchPlaceholder="Search categories…"
          emptyMessage="No categories found."
          className="w-full"
        />
      </DetailRow>
      <DetailRow icon={Tags} label="Sub-code">
        <SubCategoryPicker
          categoryId={detail.categoryId}
          categories={categories}
          value={subCategory}
          onChange={(next) => {
            setSubCategory(next);
            if (next !== (detail.subCategory ?? "")) {
              onPatch({ subCategory: next || null });
            }
          }}
          className="w-full"
        />
      </DetailRow>
      <DetailRow icon={Clock} label="Time">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            step={1}
            value={units}
            onChange={(e) => setUnits(e.target.value)}
            onBlur={commitUnits}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            className="h-8 w-20 text-xs tabular-nums"
            aria-label="Units"
          />
          <span className="text-xs text-muted-foreground">
            {formatUnitsAsHours(detail.units, unitMinutes)}
          </span>
        </div>
      </DetailRow>
      {/* Firm time is never billed and carries no matter phase, so the
          billable flag and project code do not apply to it. */}
      {!isFirm && (
        <>
          <DetailRow icon={Flag} label="Billable">
            <Switch
              checked={detail.billable}
              onCheckedChange={(billable) => onPatch({ billable })}
              aria-label="Billable"
            />
          </DetailRow>
          <DetailRow icon={Layers} label="Project code">
            <Select
              value={detail.projectCode}
              onValueChange={(value) =>
                onPatch({ projectCode: value as ProjectCode })
              }
            >
              <SelectTrigger className="h-8 w-44 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="post">Post-appointment</SelectItem>
                <SelectItem value="pre">Pre-appointment</SelectItem>
              </SelectContent>
            </Select>
          </DetailRow>
        </>
      )}
    </>
  );
}

function NarrativeField({
  value,
  units,
  unitMinutes,
  orgId,
  onCommit,
}: {
  value: string;
  units: number;
  unitMinutes: number;
  orgId: Id<"organizations">;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  // The box saves on blur, and pressing the mic is a blur, so a dictated
  // transcript saves itself as soon as it lands rather than waiting for a
  // click somewhere else (MOR-1456).
  const dictation = useDictatedField({
    value: draft,
    onChange: (next) => {
      setDraft(next);
      const trimmed = next.trim();
      if (trimmed && trimmed !== value) onCommit(trimmed);
    },
  });

  return (
    <>
      <div className="mt-1.5 flex items-start gap-1">
        <Textarea
          value={dictation.displayValue}
          readOnly={dictation.readOnly}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            const trimmed = draft.trim();
            // Every entry needs a narrative, so an emptied box reverts rather
            // than failing the mutation.
            if (trimmed && trimmed !== value) onCommit(trimmed);
            else setDraft(value);
          }}
          rows={3}
          className="text-sm"
          aria-label="Narrative"
          {...NARRATIVE_INPUT_PROPS}
        />
        <DictationButton voice={dictation.voice} />
      </div>
      <NarrativeWarningNote
        narrative={draft}
        units={units}
        unitMinutes={unitMinutes}
        orgId={orgId}
        className="mt-1"
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Layout helpers — the label/value rhythm of the task panel's Details tab
// ---------------------------------------------------------------------------

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-2">
      <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="size-3.5" />
        {label}
      </Label>
      <div className="min-w-0 text-sm text-foreground">{children}</div>
    </div>
  );
}

function Callout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-1 rounded-md border p-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-destructive">
        {title}
      </p>
      <p className="whitespace-pre-wrap text-sm text-foreground">{children}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Change history — the shared feed used by every other detail panel
// ---------------------------------------------------------------------------

function EntryHistory({
  history,
}: {
  history: {
    timestamp: number;
    actor: string;
    userId?: Id<"users">;
    action: string;
    reason: string;
    previousValues?: unknown;
    newValues?: unknown;
  }[];
}) {
  // The query returns newest-first; `ChangeHistory` reverses its input, so
  // hand it the oldest-first order it expects.
  const entries = useMemo<ChangeHistoryEntry[]>(
    () =>
      [...history].reverse().map((h) => ({
        timestamp: h.timestamp,
        actor: h.actor,
        userId: h.userId,
        action: h.action,
        reason: h.reason,
        previousValues: h.previousValues as Record<string, unknown> | undefined,
        newValues: h.newValues as Record<string, unknown> | undefined,
      })),
    [history],
  );

  const authorIds = useMemo(() => {
    const ids = new Set<Id<"users">>();
    for (const e of entries) if (e.userId) ids.add(e.userId);
    return Array.from(ids);
  }, [entries]);

  const { data: authors } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, { ids: authorIds }),
    enabled: authorIds.length > 0,
  });

  const usersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of authors ?? []) map.set(u._id, u);
    return map;
  }, [authors]);

  if (entries.length === 0) {
    return (
      <p className="rounded-lg border border-dashed bg-muted/10 p-6 text-center text-sm text-muted-foreground">
        No changes recorded yet.
      </p>
    );
  }

  return (
    <ChangeHistory
      history={entries}
      defaultOpen
      fieldLabels={ENTRY_FIELD_LABELS}
      formatters={ENTRY_FIELD_FORMATTERS}
      usersById={usersById}
    />
  );
}

// ---------------------------------------------------------------------------
// Notes feed — same components as contact notes
// ---------------------------------------------------------------------------

function EntryNotes({
  entryId,
  currentUserId,
}: {
  entryId: Id<"timeEntries">;
  currentUserId?: Id<"users">;
}) {
  const { data: notes, isLoading } = useQuery(
    convexQuery(api.timesheets.notes.listEntryNotes, { entryId }),
  );
  const createNote = useConvexMutation(api.timesheets.notes.createEntryNote);
  const updateNote = useConvexMutation(api.timesheets.notes.updateEntryNote);
  const deleteNote = useConvexMutation(api.timesheets.notes.deleteEntryNote);

  const feedNotes: FeedNote[] = (notes ?? []).map((n) => ({
    _id: n._id as string,
    content: n.content,
    createdBy: n.createdBy,
    createdAt: n.createdAt,
    updatedAt: n.updatedAt,
    authorDisplayName: n.authorDisplayName,
    lastEditedByName: n.lastEditedByName,
  }));

  return (
    <NotesFeedView
      notes={feedNotes}
      notesCount={feedNotes.length}
      isLoading={isLoading}
      currentUserId={currentUserId}
      showSummary={false}
      showChat={false}
      placeholder="Add context for reviewers — why this time matters, what changed…"
      onPost={async (content) => {
        await createNote({ entryId, content });
      }}
      onSaveNote={async (noteId, content) => {
        await updateNote({ noteId: noteId as Id<"timeEntryNotes">, content });
      }}
      onDeleteNote={async (noteId) => {
        await deleteNote({ noteId: noteId as Id<"timeEntryNotes"> });
      }}
    />
  );
}
